import { FC, ReactNode, useMemo } from 'react';
import {
  createTheme,
  CssBaseline,
  PaletteMode,
  StyledEngineProvider,
  ThemeProvider as MUIThemeProvider,
} from '@mui/material';
import ComponentsOverrides from '@mgm/common/theme/overrides';
import CustomShadows from '@mgm/common/theme/customShadows';
import GlobalStyles from '@mgm/common/theme/globalStyles';
import { createAppPalette } from '@mgm/common/theme/palette';
import ThemeShadows from '@mgm/common/theme/shadow';
import shape from '@mgm/common/theme/shape';
import typography from '@mgm/common/theme/typography';
import { useAppSelector } from '../hooks/useAppStore';
import { getTokenDetails } from '../store/authenticate/selectors';
import { getIsPrintMode } from '../store/common/selectors';

type TThemeConfig = {
  children: ReactNode
};

const ThemeConfig: FC<TThemeConfig> = ({ children }) => {
  const { settings } = useAppSelector(getTokenDetails);
  const isPrintMode = useAppSelector(getIsPrintMode);

  const themeType = settings?.ui?.theme || 'light';

  const themeOptions = useMemo(() => {
    const basePalette = createAppPalette();

    const selectedPalette = themeType === 'light' ? basePalette.light : basePalette.dark;

    const palette = {
      ...selectedPalette,
      mode: themeType as PaletteMode,
    };

    return {
      palette,
      shape,
      shadows: ThemeShadows(palette),
      typography,
      customShadows: CustomShadows(palette),
    };
  }, [themeType, isPrintMode]);

  const theme = createTheme(themeOptions);

  theme.components = ComponentsOverrides(theme as never);

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
};

export default ThemeConfig;
